import { formatInLanguage } from '@/utils/dateLocale';
import type { RevenueDynamicsGranularity } from '@/api/models';

export type RevenueDynamicsProduct = {
    id: string;
    name: string;
    category: string;
};

/**
 * KPI tiles render order. Each entry is keyed by a slot the View
 * lays out with a localized label.
 */
export type RevenueDynamicsKpiSlot =
    | 'totalRevenue'
    | 'totalDiscounts'
    | 'totalMarkup'
    | 'marginPercent'
    | 'transactions'
    | 'averageReceipt';

export const REVENUE_DYNAMICS_KPI_SLOTS: ReadonlyArray<RevenueDynamicsKpiSlot> =
    [
        'totalRevenue',
        'totalDiscounts',
        'totalMarkup',
        'marginPercent',
        'transactions',
        'averageReceipt',
    ];

export const REVENUE_DYNAMICS_KPI_LABEL_KEY: Record<
    RevenueDynamicsKpiSlot,
    string
> = {
    averageReceipt: 'sales.widgets.revenueDynamics.kpis.averageReceipt',
    marginPercent: 'sales.widgets.revenueDynamics.kpis.marginPercent',
    totalMarkup: 'sales.widgets.revenueDynamics.kpis.totalMarkup',
    totalRevenue: 'sales.widgets.revenueDynamics.kpis.totalRevenue',
    totalDiscounts: 'sales.widgets.revenueDynamics.kpis.totalDiscounts',
    transactions: 'sales.widgets.revenueDynamics.kpis.transactions',
};

/**
 * Returns the X-axis tick label for a bucket. The server always sends
 * `date` as ISO; we format it based on `granularity` so the axis stays
 * readable regardless of bucket size.
 *
 *   - hour   → "HH:00"            (e.g. "10:00")
 *   - day    → "D MMM"            (e.g. "5 May")
 *   - week   → "D MMM"            (week start, same shape as day)
 *   - month  → "MMM YY"           (e.g. "May 26")
 *   - year   → "YYYY"             (e.g. "2026")
 *
 * Month names are rendered in `language` (the active i18n language),
 * so a Georgian UI gets "17 აგვ" rather than "17 Aug". The axis renders
 * raw strings, so the caller must re-format when the language changes
 * — `RevenueDynamicsView` keys its formatter on `i18n.language`.
 */
export const formatBucketLabel = (
    date: string,
    granularity: RevenueDynamicsGranularity,
    language?: string,
): string => {
    const format = (pattern: string) =>
        formatInLanguage(date, pattern, language);
    switch (granularity) {
        case 'hour':
            return format('HH:00');
        case 'day':
            return format('D MMM');
        case 'week':
            return format('D MMM');
        case 'month':
            return format('MMM YY');
        case 'year':
            return format('YYYY');
    }
};

/**
 * Hand-picked product fixture matching the Sales mockup vocabulary.
 * The product list isn't yet served by an endpoint; this seeds the
 * picker so the filter UX works end-to-end against the live
 * revenue-dynamics endpoint (which echoes back the `productId`).
 */
export const MOCK_REVENUE_DYNAMICS_PRODUCTS: ReadonlyArray<RevenueDynamicsProduct> =
    [
        { id: 'P_001', name: 'Milk "Sante" 1L 3.2%', category: 'Dairy' },
        {
            id: 'P_002',
            name: 'Yogurt "Sante" Strawberry 230g',
            category: 'Dairy',
        },
        { id: 'P_003', name: 'Butter "Sante" 200g 82%', category: 'Dairy' },
        { id: 'P_004', name: 'Sulguni cheese 1kg', category: 'Dairy' },
        { id: 'P_005', name: 'Mozzarella "Alma" 250g', category: 'Dairy' },
        { id: 'P_010', name: 'Shoti bread', category: 'Bakery' },
        { id: 'P_011', name: 'Lavash 5pcs', category: 'Bakery' },
        { id: 'P_012', name: 'French baguette 250g', category: 'Bakery' },
        {
            id: 'P_013',
            name: 'Croissant with chocolate 80g',
            category: 'Bakery',
        },
        { id: 'P_020', name: 'Chicken fillet 1kg', category: 'Meat' },
        { id: 'P_021', name: 'Beef tenderloin 1kg', category: 'Meat' },
        { id: 'P_022', name: 'Bacon "Nikora" 150g', category: 'Meat' },
        { id: 'P_023', name: 'Sausage "Nikora" Sadilo 400g', category: 'Meat' },
        {
            id: 'P_030',
            name: 'Borjomi 0.5L (glass)',
            category: 'Water & Juices',
        },
        { id: 'P_031', name: 'Nabeghlavi 1L', category: 'Water & Juices' },
        {
            id: 'P_032',
            name: 'Apple juice "Sante" 1L',
            category: 'Water & Juices',
        },
        { id: 'P_033', name: 'Coca-Cola 1.5L', category: 'Soft Drinks' },
        {
            id: 'P_034',
            name: 'Natakhtari lemonade 1L',
            category: 'Soft Drinks',
        },
        { id: 'P_035', name: 'Fanta 1L', category: 'Soft Drinks' },
        { id: 'P_040', name: 'Apple "Golden" 1kg', category: 'Fruit & Veg' },
        { id: 'P_041', name: 'Banana 1kg', category: 'Fruit & Veg' },
        { id: 'P_042', name: 'Tomato 1kg', category: 'Fruit & Veg' },
        { id: 'P_043', name: 'Cucumber 1kg', category: 'Fruit & Veg' },
        {
            id: 'P_050',
            name: 'Chips "Chito Gvrito" salt 80g',
            category: 'Snacks',
        },
        { id: 'P_051', name: 'Pistachios salted 200g', category: 'Snacks' },
        { id: 'P_052', name: 'Hazelnut raw 200g', category: 'Snacks' },
        {
            id: 'P_060',
            name: 'Coffee "Lavazza" beans 250g',
            category: 'Tea & Coffee',
        },
        {
            id: 'P_061',
            name: 'Tea "Liptoni" black 25pcs',
            category: 'Tea & Coffee',
        },
    ];

/** Index for fast `id → product` lookup in the picker badge. */
export const findRevenueDynamicsProduct = (
    id: string | null,
): RevenueDynamicsProduct | null => {
    if (id === null) return null;
    return (
        MOCK_REVENUE_DYNAMICS_PRODUCTS.find((product) => product.id === id) ??
        null
    );
};

const COMPACT_ONE_DECIMAL = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
});

const COMPACT_WHOLE = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
});

/**
 * A revenue axis tick, abbreviated so the axis stays narrow: `1.2M`, `450k`,
 * `120`.
 *
 * Lives here rather than in the View because a View formats nothing of its own
 * (BOS-162 moved the arithmetic out of the render). Not money: an axis tick is
 * a scale marker, so it carries no currency symbol — the tooltip beside it does.
 */
export const formatCompactTick = (value: number): string => {
    const magnitude = Math.abs(value);
    if (magnitude >= 1_000_000) {
        return `${COMPACT_ONE_DECIMAL.format(value / 1_000_000)}M`;
    }
    if (magnitude >= 1_000) {
        return `${COMPACT_WHOLE.format(value / 1_000)}k`;
    }
    return String(value);
};

/** Shared responsive grid: the desktop columns follow the number of slots. */
export const REVENUE_DYNAMICS_KPI_GRID_CLASS =
    'grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-none xl:grid-flow-col xl:auto-cols-fr';

export const REVENUE_DYNAMICS_KPI_DECORATION: Partial<
    Record<RevenueDynamicsKpiSlot, { icon: string; subtitleKey: string }>
> = {
    totalDiscounts: {
        icon: 'gravity-ui:tag',
        subtitleKey: 'sales.widgets.revenueDynamics.discountedSales',
    },
};
